import type { CommandActionParams, CommandExitParams } from '../../types/CommandAction.js';
import {
  createApiContext,
  apiServerPort,
  waitForBusData,
  linkConfigSource,
  linkPathsSource,
  linkDoneSource,
  linkDisconnectedSource,
  type LinkPathOutput,
  type Context,
  type CloudpackServer,
} from '@ms-cloudpack/api-server';
import { writeJson } from '@ms-cloudpack/json-utilities';
import path from 'path';
import type { CloudpackConfig, CloudpackLinkConfig } from '@ms-cloudpack/common-types';
import { filterInternalCapabilities } from '@ms-cloudpack/bundler-capabilities';
import { createLinkResolveMap, mergeObjects } from '@ms-cloudpack/package-utilities';
import { createApiServer } from '../../utilities/createApiServer.js';
import { openBrowser } from '../start/openBrowser.js';
import type { LinkOptions } from './types/LinkOptions.js';
import type { SharedOptions } from '../../types/SharedOptions.js';
import { resolveCloudpackInternalPath } from '../../utilities/resolveCloudpackInternalPath.js';
import { createRemoteApiUrls } from './createRemoteApiUrls.js';
import { disposeServer } from '../../utilities/disposeServer.js';

/**
 * Handles the remote linking process
 */
export async function linkToRemoteSession(
  params: CommandActionParams<LinkOptions & SharedOptions>,
): Promise<CommandExitParams> {
  const { options, appPath, autoDispose } = params;
  const {
    remote,
    ignore: ignoredPackages,
    all: includeAll,
    ignoreResolutions,
    resolveStrategy,
    logResolveMap,
  } = options;

  const linkProxyPath = await resolveCloudpackInternalPath({
    importMetaUrl: import.meta.url,
    packageName: '@ms-cloudpack/link-proxy',
  });
  if (!linkProxyPath) {
    return { hasErrors: true, message: 'Failed to resolve link proxy path' };
  }

  // Override routes to default to serve link proxy instead.
  // TODO: Should this be done earlier to avoid the side effect that happens while reading the config?
  delete params.config.routes;

  const context = autoDispose(
    await createApiContext({
      ...params,
      // Load the link proxy to the resolve map to load it later.
      additionalPaths: [linkProxyPath],
      // Skip resolving dependencies, as we will load the resolve map later.
      skipResolveDependencies: true,
    }),
  );

  const { session, packages, packageHashes, bus } = context;

  // Start api server for tracking status and handling remote requests.
  const apiServerResult = await createApiServer(
    // Create the api server at different ports to avoid conflicts.
    { port: apiServerPort.map((p) => p + apiServerPort.length) },
    context,
  );
  if (apiServerResult.hasErrors) {
    return apiServerResult;
  }

  const { apiServer } = apiServerResult;
  autoDispose({ dispose: () => disposeServer('API server', apiServer) });

  // Get possible host api server urls.
  session.urls.hostApiServer = createRemoteApiUrls({ remote, apiServerUrl: apiServer.url });

  // Then start bundle and app servers for hosting the app.
  const { startServers } = await import('@ms-cloudpack/app-server');
  const servers = await startServers(
    {
      // Start the app server with the link proxy.
      definition: await packages.get(linkProxyPath),
      bundleServerOptions: { disableCache: !options.cache },
    },
    context,
  );
  autoDispose({ dispose: () => disposeServer('app server', servers.appServer) });
  autoDispose({ dispose: () => disposeServer('bundle server', servers.bundleServer) });

  if (!session.urls.appServer || !session.urls.bundleServer) {
    return { hasErrors: true, message: 'Failed to start app server' };
  }

  void openBrowser({ url: session.urls.appServer }, context).catch((err: unknown) => {
    console.warn('Error opening browser:', (err as Error | undefined)?.stack || err);
    console.warn('Please open the browser manually to:', session.urls.appServer);
  });

  // Setup disconnection handling
  const { disconnectionPromise, withDisconnectionHandling } = createDisconnectionHandler(bus);

  // Get config from host api server.
  console.debug('Attempting to fetch host config from the bus.');
  const hostConfigResult = await withDisconnectionHandling(
    waitForBusData<CloudpackLinkConfig>({ bus, source: linkConfigSource }),
  );

  // Check if disconnection happened
  if (hostConfigResult.disconnected) {
    return hostConfigResult.result;
  }

  const hostConfig = hostConfigResult.result as CloudpackConfig;

  // This exclusive to the local repo.
  delete hostConfig?.resolve;

  hostConfig.packageSettings?.forEach((settings) => {
    // We need to delete bundler capabilities that are not internal as they won't work.
    // TODO: Figure out a way to support them.
    if (settings.bundlerCapabilities) {
      settings.bundlerCapabilities = filterInternalCapabilities(settings.bundlerCapabilities);
    }
  });

  console.debug('Host config received:', hostConfig);
  // Merge host config with local config.
  const mergedConfig = mergeObjects([hostConfig, params.config]);
  session.config = mergedConfig;

  packages.reset({ newConfig: mergedConfig });

  console.debug('Searching for packages from paths:', appPath);

  // Use settings to find partial link app map.
  // Update the resolve map with the linked packages.
  context.session.resolveMap = await createLinkResolveMap({ appPath, additionalPaths: [linkProxyPath] }, context);

  bus.publish(linkPathsSource, {
    linkedPath: {
      path: appPath,
      ignoredPackages,
      includeAll,
      ignoreResolutions,
      resolveStrategy,
      remote: {
        // Host app must route linked paths to the bundle server.
        hostUrl: session.urls.bundleServer,
        // Linked session resolve map to resolve dependencies on the host.
        resolveMap: context.session.resolveMap,
        // Send the package definitions to create the import map.
        packages: packages.toSerializable(),
        // We must send all external hashes for the linked packages and then update internal hashes on change.
        packageHashes: packageHashes.toSerializable(),
      },
    },
  });

  // Get result from host.
  console.debug('Waiting for host to link paths...');
  const linkPathResult = await withDisconnectionHandling(
    waitForBusData<LinkPathOutput>({ bus, source: linkDoneSource }),
  );

  // Check if disconnection happened
  if (linkPathResult.disconnected) {
    return linkPathResult.result;
  }

  const result = linkPathResult.result;
  console.debug('Host linked paths:', result.linkedPaths);

  // Monitor client connections
  const connectionMonitor = setupConnectionMonitor(apiServer, session.urls.appServer);
  autoDispose({ dispose: () => clearInterval(connectionMonitor) });

  // Wait for the host to send back the resolve map and linked paths.
  if (logResolveMap) {
    // Write the linked resolve map to disk.
    await writeJson(path.join(appPath, 'resolve-map-linked.json'), result.resolveMap);
  }

  console.debug('Linked paths:', result.linkedPaths);

  return disconnectionPromise;
}

/**
 * Creates a disconnection handler that wraps promises
 */
function createDisconnectionHandler(bus: Context['bus']) {
  // Create a promise that will resolve when host disconnection is detected
  const disconnectionPromise = new Promise<CommandExitParams>((res) => {
    // Listen for host disconnection events
    bus.subscribe(linkDisconnectedSource, ({ reason }: { reason: string }) => {
      res({ hasErrors: true, message: `Host connection ${reason}. Shutting down link session.` });
    });
  });

  async function withDisconnectionHandling<T>(
    operation: Promise<T>,
  ): Promise<{ disconnected: false; result: T } | { disconnected: true; result: CommandExitParams }> {
    return await Promise.race([
      operation.then((result): { disconnected: false; result: T } => ({ disconnected: false, result })),
      disconnectionPromise.then((result): { disconnected: true; result: CommandExitParams } => ({
        disconnected: true,
        result,
      })),
    ]);
  }

  return { disconnectionPromise, withDisconnectionHandling };
}

/**
 * Setup client connection monitoring
 */
function setupConnectionMonitor(apiServer: CloudpackServer, appServerUrl: string) {
  let hadConnections = false;
  const connectionMonitor = setInterval(() => {
    const connectedClients = apiServer.clients.size;

    // If we previously had connections and now have none
    if (hadConnections && connectedClients === 0) {
      console.warn('Browser connection lost. Please reopen your browser to:', appServerUrl);
      hadConnections = false;
    } else if (connectedClients > 0) {
      hadConnections = true;
    }
  }, 5000); // Check every 5 seconds

  return connectionMonitor;
}
